import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import {
  borderStyleTokens,
  borderWidthTokens,
  colorTokens,
  effectTokens,
  radiusTokens,
  shadowTokens,
  spacingTokens,
} from '@/shared/lib/catalog'
import {
  fontFamilyTokens,
  sampleText,
  tokenSectionMeta,
  tokenSections,
  typeScaleTokens,
} from '../lib/catalog-utils'

type CopyState = {
  tokenPath: string
  message: string
}

export function CatalogTokens() {
  const [copyState, setCopyState] = useState<CopyState | null>(null)

  const handleCopy = async (tokenPath: string, tokenValue: string) => {
    if (!navigator.clipboard) {
      setCopyState({ tokenPath, message: 'Copy not supported in this browser.' })
      return
    }

    try {
      await navigator.clipboard.writeText(`${tokenPath}: ${tokenValue}`)
      setCopyState({ tokenPath, message: 'Copied.' })
      window.setTimeout(() => {
        setCopyState((prev) => (prev?.tokenPath === tokenPath ? null : prev))
      }, 2000)
    } catch (error) {
      console.error(error)
      setCopyState({ tokenPath, message: 'Unable to copy.' })
    }
  }

  const renderCopyButton = (tokenPath: string, tokenValue: string) => {
    const isActive = copyState?.tokenPath === tokenPath
    return (
      <button
        type="button"
        className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-primary/60 hover:text-foreground"
        onClick={() => handleCopy(tokenPath, tokenValue)}
      >
        {isActive ? copyState?.message : 'Copy'}
      </button>
    )
  }

  return (
    <div className="mt-10 space-y-10 px-6">
      <div className="space-y-2">
        <h2 className="text-foreground" style={typographyStyle(designTokens.typography.heading.h2)}>
          Tokens & Design Primitives
        </h2>
        <p
          className="max-w-3xl text-muted-foreground"
          style={typographyStyle(designTokens.typography.body.sm)}
        >
          Raw token values sourced from design-tokens.json, grouped by system category for quick
          reference.
        </p>
      </div>

      <div className="grid gap-10 lg:grid-cols-[220px,1fr]">
        <aside className="h-fit rounded-xl border border-border bg-card/60 p-4 lg:sticky lg:top-24">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sections
          </p>
          <nav aria-label="Token sections" className="mt-4 flex flex-col gap-2 text-sm">
            {tokenSections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="rounded-md px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {section.label}
              </a>
            ))}
          </nav>
        </aside>

        <div className="space-y-12">
          <section id="colors" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Colors</h3>
              <p className="text-sm text-muted-foreground">{tokenSectionMeta.colors.description}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {colorTokens.map((token) => (
                <Card key={token.path} className="overflow-hidden">
                  <CardContent className="flex items-center gap-4 p-4">
                    <div
                      className="h-12 w-12 rounded-md border border-border"
                      style={{ background: token.value }}
                    />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold capitalize">{token.name}</p>
                      <p className="text-xs text-muted-foreground">{token.path}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-mono text-foreground">{token.value}</p>
                        {renderCopyButton(token.path, token.value)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="typography" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Typography</h3>
              <p className="text-sm text-muted-foreground">
                {tokenSectionMeta.typography.description}
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <h4 className="text-lg font-semibold">Font families</h4>
                <div className="grid gap-4 lg:grid-cols-2">
                  {fontFamilyTokens.map((token) => (
                    <Card key={token.path}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold capitalize">
                          {token.name}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">{token.path}</p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-mono text-foreground">{token.value}</p>
                          {renderCopyButton(token.path, token.value)}
                        </div>
                        <p
                          className="text-sm"
                          style={{
                            fontFamily: token.meta.fontFamily,
                            fontSize: token.meta.fontSize,
                            fontWeight: token.meta.fontWeight,
                            lineHeight: token.meta.lineHeight,
                          }}
                        >
                          {sampleText}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-lg font-semibold">Type scales</h4>
                <div className="grid gap-4 lg:grid-cols-2">
                  {typeScaleTokens.map((token) => (
                    <Card key={token.path}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold capitalize">
                          {token.name}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">{token.path}</p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-mono text-foreground">{token.value}</p>
                          {renderCopyButton(token.path, token.value)}
                        </div>
                        <p
                          className="text-sm"
                          style={{
                            fontFamily: token.meta.fontFamily,
                            fontSize: token.meta.fontSize,
                            fontWeight: token.meta.fontWeight,
                            lineHeight: token.meta.lineHeight,
                            letterSpacing: token.meta.letterSpacing,
                          }}
                        >
                          {sampleText}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="spacing" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Spacing</h3>
              <p className="text-sm text-muted-foreground">
                {tokenSectionMeta.spacing.description}
              </p>
            </div>
            <div className="space-y-3">
              {spacingTokens.map((token) => (
                <Card key={token.path}>
                  <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold capitalize">{token.name}</p>
                      <p className="text-xs text-muted-foreground">{token.path}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-mono text-foreground">{token.value}</p>
                        {renderCopyButton(token.path, token.value)}
                      </div>
                    </div>
                    <div
                      className="h-2 rounded-full bg-primary/70"
                      style={{ minWidth: '12px', width: token.value }}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="radii" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Radii</h3>
              <p className="text-sm text-muted-foreground">{tokenSectionMeta.radii.description}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {radiusTokens.map((token) => (
                <Card key={token.path}>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div
                      className="h-12 w-12 border border-border bg-muted/40"
                      style={{ borderRadius: token.value }}
                    />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold capitalize">{token.name}</p>
                      <p className="text-xs text-muted-foreground">{token.path}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-mono text-foreground">{token.value}</p>
                        {renderCopyButton(token.path, token.value)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="shadows" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Shadows</h3>
              <p className="text-sm text-muted-foreground">
                {tokenSectionMeta.shadows.description}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {shadowTokens.map((token) => (
                <Card key={token.path}>
                  <CardContent className="space-y-3 p-4">
                    <div
                      className="h-16 rounded-md border border-border bg-background"
                      style={{ boxShadow: token.value === 'none' ? undefined : token.value }}
                    />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold capitalize">{token.name}</p>
                      <p className="text-xs text-muted-foreground">{token.path}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-mono text-foreground">{token.value}</p>
                        {renderCopyButton(token.path, token.value)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="borders" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Borders</h3>
              <p className="text-sm text-muted-foreground">
                {tokenSectionMeta.borders.description}
              </p>
            </div>
            <div className="space-y-6">
              <div className="space-y-3">
                <h4 className="text-lg font-semibold">Widths</h4>
                <div className="space-y-3">
                  {borderWidthTokens.map((token) => (
                    <Card key={token.path}>
                      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold capitalize">{token.name}</p>
                          <p className="text-xs text-muted-foreground">{token.path}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-mono text-foreground">{token.value}</p>
                            {renderCopyButton(token.path, token.value)}
                          </div>
                        </div>
                        <div
                          className="w-24 border-b border-foreground"
                          style={{ borderBottomWidth: token.value }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="text-lg font-semibold">Styles</h4>
                <div className="space-y-3">
                  {borderStyleTokens.map((token) => (
                    <Card key={token.path}>
                      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold capitalize">{token.name}</p>
                          <p className="text-xs text-muted-foreground">{token.path}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-mono text-foreground">{token.value}</p>
                            {renderCopyButton(token.path, token.value)}
                          </div>
                        </div>
                        <div
                          className="h-6 w-24 border border-foreground"
                          style={{ borderStyle: token.value, borderWidth: '2px' }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="effects" className="scroll-mt-24 space-y-4">
            <div>
              <h3 className="text-2xl font-semibold">Global Effects</h3>
              <p className="text-sm text-muted-foreground">
                {tokenSectionMeta.effects.description}
              </p>
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),320px]">
              <Card className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Duotone noise overlay</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Applied globally to all screens; preview shows noise on a gradient surface.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="duotone-noise-swatch h-32 w-full rounded-lg border border-border bg-[linear-gradient(130deg,#f41ca6_0%,#fb93f1_40%,#ffffff_100%)]" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Parameters</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Noise size, density, blend mode, and duotone tints from Figma.
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Size</span>
                    <span className="font-mono text-foreground">1</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Density</span>
                    <span className="font-mono text-foreground">65%</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Blend mode</span>
                    <span className="font-mono text-foreground">overlay</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Light tint</span>
                    <span className="font-mono text-foreground">rgba(255, 255, 255, 0.35)</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Accent tint</span>
                    <span className="font-mono text-foreground">rgba(237, 43, 189, 0.1)</span>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {effectTokens.map((token) => (
                <Card key={token.path}>
                  <CardContent className="space-y-2 p-4">
                    <p className="text-sm font-semibold capitalize">{token.name}</p>
                    <p className="text-xs text-muted-foreground">{token.path}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-mono text-foreground">{token.value}</p>
                      {renderCopyButton(token.path, token.value)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
